import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authed, object = authed
  const [user, setUser] = useState(null);
  const lastResumeRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      // Instant activation: if logged in but not yet marked subscribed, reconcile
      // with Stripe once per session (covers the brief window before the webhook).
      if (data && !data.subscribed && !sessionStorage.getItem("subSynced")) {
        sessionStorage.setItem("subSynced", "1");
        try {
          const res = await axios.post(`${API}/subscription/sync`, {}, { withCredentials: true });
          if (res.data?.subscribed) {
            const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(me.data);
            return me.data;
          }
        } catch {
          /* ignore — webhook remains the backup */
        }
      }
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Re-check auth when the app is resumed (bfcache restore or regaining
    // visibility) so the installed PWA never runs on stale session state.
    // Throttled to avoid hammering /auth/me on every tab switch.
    const onPageShow = (e) => {
      if (e.persisted) {
        lastResumeRef.current = Date.now();
        refresh();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastResumeRef.current > 15000) {
        lastResumeRef.current = Date.now();
        refresh();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/archive";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem("subSynced");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refresh, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
